import type { Falha, Resultado } from './acesso';
import { buscar } from './buscar';

/**
 * ENCOMENDAS — cliente HTTP da tela. Quem decide o que cada um vê é o servidor: custo e margem
 * chegam `null` para quem não tem `loja.ver` (regra permanente do dono); a tela só esconde a linha.
 */

async function pedir<T>(caminho: string): Promise<Resultado<T>> {
  let r: Response;
  try {
    r = await buscar(caminho, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

/** Bloco de uma fonte: falha SOZINHO (a coluna mostra o erro e "tentar de novo"). */
export type Bloco<T> =
  | { readonly ok: true; readonly lidoEm: string; readonly dados: T }
  | { readonly ok: false; readonly motivo: string; readonly mensagem: string };

export interface ProdutoDaConsulta {
  readonly id: string;
  readonly descricao: string;
  readonly fabricante: string | null;
  readonly principioAtivo: string | null;
  readonly classificacao: string | null;
  readonly ean: string | null;
  readonly codigoHos: string | null;
  readonly estoque: string | null;
  readonly estoqueVistoEm: string | null;
  readonly pmcCadastro: string | null;
  readonly foto: string | null;
  readonly exigeReceita: boolean;
  readonly ativoNoHos: boolean;
}

export interface ProdutoAoVivo {
  readonly precoVenda: string | null;
  readonly pmc: string | null;
  readonly descontoAtePct: number;
  readonly estoque: number | null;
  readonly statusNoHos: string | null;
  readonly promocoes: readonly string[];
}

export interface CompraDoHistorico {
  readonly tipo: 'PEDIDO' | 'ENTRADA';
  readonly data: string | null;
  readonly fornecedor: string | null;
  readonly quantidade: number | null;
  readonly custoUnitario: string | null;
  readonly custoTotal: string | null;
  readonly pedido: string | null;
  readonly comprador: string | null;
  readonly status: string | null;
  readonly previsao: string | null;
}

export interface HistoricoDeCompras {
  readonly compras: readonly CompraDoHistorico[];
  readonly ultima: CompraDoHistorico | null;
  readonly indicadores: {
    readonly menorCusto: string | null;
    readonly custoMedio: string | null;
    readonly maiorCusto: string | null;
    readonly quantidadeTotal: number;
  };
}

export type MarcadorDaNota = 'NF_EMITIDA' | 'EM_PROCESSAMENTO' | 'ENTROU_NO_HOS' | 'CONFERIDA';

export interface NotaDoProduto {
  readonly data: string | null;
  readonly numero: string | null;
  readonly chave: string | null;
  readonly fornecedor: string | null;
  readonly cnpj: string | null;
  readonly quantidade: number | null;
  readonly custoUnitario: string | null;
  readonly bonificacao: boolean;
  readonly pedido: string | null;
  readonly marcador: MarcadorDaNota;
  readonly fonte: 'HOS' | 'INOVAPED';
  readonly faturadaEm: string | null;
  readonly chegouEm: string | null;
  readonly entradaEm: string | null;
}

export interface NotasDoProduto {
  readonly notas: readonly NotaDoProduto[];
  readonly hosIndisponivel: { readonly motivo: string; readonly mensagem: string } | null;
}

export type TomDaDisponibilidade = 'verde' | 'ambar' | 'vermelho' | 'neutro';

export interface OfertaDaDistribuidora {
  readonly distribuidora: string;
  readonly slug: string | null;
  readonly situacao: 'DISPONIVEL' | 'DISPONIVEL_SEM_QUANTIDADE' | 'SEM_ESTOQUE' | 'NAO_INFORMADO';
  readonly tom: TomDaDisponibilidade;
  readonly disponivel: boolean;
  readonly estoque: number | null;
  readonly custo: string | null;
  readonly pmc: string | null;
  readonly precoInova: string | null;
  readonly margemEstimada: string | null;
  readonly condicaoComercial: string | null;
  readonly descontoPct: number | null;
  readonly prazoDias: number | null;
  readonly pedidoMinimo: number | null;
  readonly atualizadoEm: string | null;
  readonly idadeMin: number | null;
  readonly velha: boolean;
}

export interface DisponibilidadeAgora {
  readonly procurando: boolean;
  readonly ofertas: readonly OfertaDaDistribuidora[];
}

export const buscarProdutos = (q: string) =>
  pedir<{ produtos: readonly ProdutoDaConsulta[] }>(
    `/api/encomendas/produtos?q=${encodeURIComponent(q.trim())}`,
  );

export const lerProduto = (id: string) =>
  pedir<{ produto: ProdutoDaConsulta; aoVivo: Bloco<ProdutoAoVivo | null> }>(
    `/api/encomendas/produtos/${id}`,
  );

export const lerHistorico = (id: string) =>
  pedir<{ historico: Bloco<HistoricoDeCompras> }>(`/api/encomendas/produtos/${id}/historico`);

export const lerNotas = (id: string) =>
  pedir<{ notas: Bloco<NotasDoProduto> }>(`/api/encomendas/produtos/${id}/notas`);

export const lerDistribuidoras = (id: string, atualizar = false) =>
  pedir<{ disponibilidade: Bloco<DisponibilidadeAgora> }>(
    `/api/encomendas/produtos/${id}/distribuidoras${atualizar ? '?atualizar=1' : ''}`,
  );

/* ------------------------------------------------------------------ *
 * Formatos
 * ------------------------------------------------------------------ */

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
export const emReais = (v: string | null | undefined): string =>
  v === null || v === undefined || v === '' ? '—' : BRL.format(Number(v));

export function diaCurto(iso: string | null): string {
  if (!iso) return '—';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return d && m && a ? `${d}/${m}/${a}` : '—';
}

export interface PassoDaNota {
  readonly rotulo: string;
  readonly feito: boolean;
  readonly texto: string;
}

/** O passo a passo que o balconista lê: faturou → chegou na loja → entrada no estoque. */
export function passosDaNota(n: NotaDoProduto): readonly PassoDaNota[] {
  const dia = (d: string | null) => (d ? diaCurto(d) : 'Sem data');
  const semEntrada = !n.chegouEm
    ? 'Ainda não'
    : n.marcador === 'EM_PROCESSAMENTO'
      ? 'Em conferência'
      : 'Falta dar entrada';
  return [
    { rotulo: 'Faturou', feito: true, texto: dia(n.faturadaEm) },
    {
      rotulo: 'Chegou na loja',
      feito: n.chegouEm !== null,
      texto: n.chegouEm ? dia(n.chegouEm) : 'Ainda não',
    },
    {
      rotulo: 'Entrada no estoque',
      feito: n.entradaEm !== null,
      texto: n.entradaEm ? dia(n.entradaEm) : semEntrada,
    },
  ];
}

const DIAS_DO_RESUMO = 30;

/**
 * A nota que vai para o topo da tela (no celular a coluna de notas fica noutra aba): a mais nova
 * ainda em andamento; sem nenhuma, a mais nova. Nota com mais de 30 dias não diz nada de hoje.
 */
export function notaDoResumo(
  notas: readonly NotaDoProduto[],
  agora = new Date(),
): NotaDoProduto | null {
  const limite = new Date(agora.getTime() - DIAS_DO_RESUMO * 86_400_000).toISOString().slice(0, 10);
  const recentes = notas
    .filter((n) => (n.data ?? '') >= limite)
    .sort((a, b) => (b.data ?? '').localeCompare(a.data ?? ''));
  return recentes.find((n) => n.entradaEm === null) ?? recentes[0] ?? null;
}

/** "agora", "há 2 min", "há 3 h", "há 4 dias". */
export function haQuanto(minutos: number | null): string {
  if (minutos === null) return 'sem data';
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${String(minutos)} min`;
  const h = Math.round(minutos / 60);
  if (h < 48) return `há ${String(h)} h`;
  return `há ${String(Math.round(h / 24))} dias`;
}

export function minutosDesde(iso: string | null, agora = new Date()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.max(0, Math.round((agora.getTime() - t) / 60_000)) : null;
}

export function textoDaFalha(f: Falha): string {
  switch (f.tipo) {
    case 'offline':
      return 'Sem conexão com o InovaPay.';
    case 'sem-acesso':
      return 'Seu acesso não abre esta consulta.';
    case 'sem-sessao':
      return 'Sua sessão terminou. Entre de novo.';
    default:
      return 'Não foi possível carregar.';
  }
}
